<template>
	<div class="phone-input" :class="{ 'phone-input--success-icon': showSuccess && hasInteracted && isValid }">
		<div class="phone-input__input-wrap">
			<MazPhoneNumberInput
				v-model="phoneNumber"
				class="phone-input__input"
				no-example
				country-locale="pl-PL"
				:error="taken || shouldShowError"
				:translations="translations"
				show-code-on-list
				:orientation="isMobile_xs ? 'col' : 'row'"
				noValidationSuccess
				:disabled="disabled"
				country-code="PL"
				:class="{ 'phone-input--success': isValid }"
				:preferredCountries="['PL', 'GB', 'DE', 'NL', 'NO', 'IE', 'US', 'SE', 'BE', 'DK']"
				@update="results = $event"
				@blur="hasInteracted = true"
				@input="hasInteracted = true">
				<template #selector-flag="{ countryCode }">
					<div class="phone-input__flag phone-input__flag--selected">
						<GlobalIcon :height="28" prefix="country" :name="countryCode"></GlobalIcon>
					</div>
				</template>
				<template #country-list-flag="{ countryCode }">
					<div class="phone-input__flag">
						<GlobalIcon :height="28" prefix="country" :name="countryCode"></GlobalIcon>
					</div>
				</template>
			</MazPhoneNumberInput>
			<div v-if="showSuccess" class="phone-input__success">
				<GlobalIcon name="tick-circle" :height="18"></GlobalIcon>
			</div>
		</div>
		<div class="phone-input__error" :class="{ 'phone-input__error--empty': !shouldShowError || taken }">
			<TransitionGroup name="fade-down">
				<div v-if="shouldShowError && !taken" class="phone-input__error-message">
					{{ errorMessage }}
				</div>
			</TransitionGroup>
		</div>
	</div>
</template>
<script setup lang="ts">
import type { Result } from 'maz-ui/components/MazPhoneNumberInput'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'

const phoneNumber = defineModel<string>()
const { isMobile_xs } = useWindowWidth()
const $t = useI18n().t
const results = ref<Result>()
const hasInteracted = inject('hasInteracted', ref(false))
const emit = defineEmits<{
	(e: 'update', v: boolean): void
}>()

const props = defineProps<{
	disabled?: boolean
	taken?: boolean
	customMessage?: string
	showSuccess?: boolean
}>()

const isValid = computed(() => {
	if (props.taken) return false
	if (!results.value?.isValid) return false
	if (!phoneNumber.value) return false

	const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber.value)

	if (!parsedPhoneNumber) return false

	const type = parsedPhoneNumber.getType()

	if (type !== 'MOBILE') return false

	return true
})

const shouldShowError = computed(() => hasInteracted.value && !isValid.value)

const errorMessage = computed(() => {
	if (!results.value?.isValid) return props.customMessage ? props.customMessage : $t('validation.enter_valid_phone_number')

	const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber.value || '')
	if (parsedPhoneNumber?.getType() !== 'MOBILE') {
		return $t('validation.enter_mobile_number')
	}

	return props.customMessage ? props.customMessage : $t('validation.enter_valid_phone_number')
})

const translations = {
	countrySelector: {
		placeholder: $t('components.phone_input.prefix'),
		error: $t('components.phone_input.select_country'),
		searchPlaceholder: $t('components.phone_input.search'),
	},

	phoneInput: {
		placeholder: $t('components.phone_input.phone_number'),
		example: '___-___-___',
	},
}

onMounted(() => {
	setTimeout(() => {
		const inputLabels = document.querySelectorAll('.m-input-label')
		const requiredIndicator = document.createElement('span')
		if (requiredIndicator) requiredIndicator.textContent = '*'
		if (requiredIndicator) requiredIndicator.style.color = 'red'
		const requiredIndicator2 = document.createElement('span')
		if (requiredIndicator2) requiredIndicator2.textContent = '*'
		if (requiredIndicator2) requiredIndicator2.style.color = 'red'
		if (inputLabels.length === 0) return
		inputLabels[0].textContent = $t('components.phone_input.prefix')
		inputLabels[0].appendChild(requiredIndicator)
		inputLabels[1].textContent = $t('components.phone_input.phone_number')
		inputLabels[1].appendChild(requiredIndicator2)
	}, 200)
})

watch(
	() => results.value,
	() => {
		if (results.value) {
			const { isValid } = results.value
			emit('update', isValid)
		}
	},
)
</script>

<style lang="scss">
@import './PhoneInput';
@include fade-down-animation;
</style>
